import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatNumber, formatPercentage, formatDate, exportToPDF, exportToExcel } from '../utils/helpers';
import { 
  Plus, Users, Heart, Star, MessageSquare, ThumbsUp, ThumbsDown, FileText, FileSpreadsheet,
  Loader2, Wand2, ExternalLink, AlertTriangle, CheckCircle, ArrowRight, Settings, Globe, Target, Send, Copy, Trash2,
  Mail, Phone, Calendar, Clock, User, Eye, Edit, MessageCircle, Bell, RefreshCw
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import AIKeyRequiredDialog from '../components/AIKeyRequiredDialog';

const GastBelevingPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [returningGuests, setReturningGuests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [guestDialog, setGuestDialog] = useState(false);
  const [returningDialog, setReturningDialog] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [importing, setImporting] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  
  // Reminder states
  const [pendingReminders, setPendingReminders] = useState([]);
  const [reminderSettings, setReminderSettings] = useState({ reminder_days: 3, max_reminders: 2 });
  const [reminderDialog, setReminderDialog] = useState(false);
  const [bulkReminderDialog, setBulkReminderDialog] = useState(false);
  const [selectedReminders, setSelectedReminders] = useState([]);
  const [bulkReminderForm, setBulkReminderForm] = useState({ channel: 'email', sent_by: '' });
  
  // AI Key Dialog state
  const [showAIKeyDialog, setShowAIKeyDialog] = useState(false);

  const [guestForm, setGuestForm] = useState({
    date: new Date().toISOString().split('T')[0], guest_count: '', location_id: ''
  });

  const [returningForm, setReturningForm] = useState({
    name: '', email: '', phone: '', preferences: '', notes: ''
  });

  const [reviewForm, setReviewForm] = useState({
    date: new Date().toISOString().split('T')[0], platform: 'Google', rating: '', content: '', guest_name: ''
  });

  const [feedbackForm, setFeedbackForm] = useState({
    date: new Date().toISOString().split('T')[0], type: 'klacht', content: '', guest_name: ''
  });

  const [googleUrl, setGoogleUrl] = useState('');
  
  const [taskForm, setTaskForm] = useState({
    assigned_to: '', due_date: '', priority: 'hoog'
  });

  const [reviewRequests, setReviewRequests] = useState([]);
  const [reviewRequestStats, setReviewRequestStats] = useState(null);
  const [reviewCalculator, setReviewCalculator] = useState(null);
  const [requestDialog, setRequestDialog] = useState(false);
  const [requestDetailDialog, setRequestDetailDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [sendDialog, setSendDialog] = useState(false);
  const [requestForm, setRequestForm] = useState({
    guest_name: '', guest_email: '', guest_phone: '', visit_date: '', notes: '', channel: 'email', sent_by: ''
  });
  const [sendForm, setSendForm] = useState({
    channel: 'email', sent_by: ''
  });
  const [editRequestDialog, setEditRequestDialog] = useState(false);
  const [editRequestForm, setEditRequestForm] = useState({
    id: '', guest_name: '', guest_email: '', guest_phone: '', visit_date: '', notes: '', channel: 'email', sent_by: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const params = { start_date: startDate.toISOString().split('T')[0], end_date: new Date().toISOString().split('T')[0] };

      const [guestsRes, retRes, revRes, fbRes, locRes, empRes, settingsRes, requestsRes, statsRes, calcRes, remindersRes] = await Promise.all([
        api.get('/guests', { params }),
        api.get('/returning-guests'),
        api.get('/reviews'),
        api.get('/feedback'),
        api.get('/locations'),
        api.get('/employees'),
        api.get('/business-settings'),
        api.get('/review-requests'),
        api.get('/review-requests/stats'),
        api.get('/reviews/calculator'),
        api.get('/review-requests/pending-reminders').catch(() => ({ data: { pending: [], settings: {} } }))
      ]);
      setGuests(guestsRes.data);
      setReturningGuests(retRes.data);
      setReviews(revRes.data);
      setFeedback(fbRes.data);
      setLocations(locRes.data);
      setEmployees(empRes.data);
      setBusinessSettings(settingsRes.data);
      setGoogleUrl(settingsRes.data?.google_business_url || '');
      setReviewRequests(requestsRes.data);
      setReviewRequestStats(statsRes.data);
      setReviewCalculator(calcRes.data);
      setPendingReminders(remindersRes.data.pending || []);
      setReminderSettings(remindersRes.data.settings || { reminder_days: 3, max_reminders: 2 });
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoogleUrl = async () => {
    try {
      await api.post('/business-settings', null, {
        params: { google_business_url: googleUrl }
      });
      toast.success('Google Business URL opgeslagen');
      setSettingsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleGenerateDemoReviews = async () => {
    if (!businessSettings?.google_business_url) {
      toast.error('Configureer eerst de Google Business URL');
      setSettingsDialog(true);
      return;
    }
    
    setImporting(true);
    try {
      const res = await api.post('/reviews/import-google');
      const data = res.data;
      
      if (data.success) {
        toast.success(`${data.reviews_imported} demo reviews gegenereerd!`, {
          description: data.complaints_found > 0 
            ? `${data.complaints_found} klachten gevonden en toegevoegd aan Feedback` 
            : 'Dit zijn AI-gegenereerde voorbeeldreviews'
        });
        fetchData();
      } else {
        toast.error(data.error || 'Genereren mislukt');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === 'NO_API_KEY') {
        setShowAIKeyDialog(true);
      } else {
        toast.error(errorDetail || 'Genereren mislukt');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    try {
      await api.post('/guests', null, {
        params: {
          date: guestForm.date,
          guest_count: parseInt(guestForm.guest_count) || 0,
          location_id: guestForm.location_id || null
        }
      });
      toast.success('Gastenaantal toegevoegd');
      setGuestDialog(false);
      setGuestForm({ date: new Date().toISOString().split('T')[0], guest_count: '', location_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddReturning = async (e) => {
    e.preventDefault();
    try {
      await api.post('/returning-guests', null, {
        params: returningForm
      });
      toast.success('Terugkerende gast toegevoegd');
      setReturningDialog(false);
      setReturningForm({ name: '', email: '', phone: '', preferences: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    try {
      await api.post('/reviews', null, {
        params: {
          ...reviewForm,
          rating: parseFloat(reviewForm.rating) || 0
        }
      });
      toast.success('Review toegevoegd');
      setReviewDialog(false);
      setReviewForm({ date: new Date().toISOString().split('T')[0], platform: 'Google', rating: '', content: '', guest_name: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddFeedback = async (e) => {
    e.preventDefault();
    try {
      await api.post('/feedback', null, {
        params: feedbackForm
      });
      toast.success('Feedback toegevoegd');
      setFeedbackDialog(false);
      setFeedbackForm({ date: new Date().toISOString().split('T')[0], type: 'klacht', content: '', guest_name: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleSendReminder = async (requestId) => {
    try {
      await api.post(`/review-requests/${requestId}/send-reminder`);
      toast.success('Herinnering verzonden');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verzenden herinnering');
    }
  };

  const handleSendBulkReminders = async () => {
    if (selectedReminders.length === 0) {
      toast.error('Selecteer eerst verzoeken');
      return;
    }
    try {
      await api.post('/review-requests/send-bulk-reminders', selectedReminders, {
        params: {
          channel: bulkReminderForm.channel,
          sent_by: bulkReminderForm.sent_by || null
        }
      });
      toast.success(`${selectedReminders.length} herinneringen verzonden`);
      setBulkReminderDialog(false);
      setSelectedReminders([]);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verzenden herinneringen');
    }
  };

  const handleUpdateReminderSettings = async () => {
    try {
      await api.put('/business-settings/review-reminders', null, {
        params: {
          reminder_enabled: true,
          reminder_days: reminderSettings.reminder_days,
          max_reminders: reminderSettings.max_reminders
        }
      });
      toast.success('Herinneringen instellingen opgeslagen');
      setReminderDialog(false);
    } catch (error) {
      toast.error('Fout bij opslaan instellingen');
    }
  };

  const toggleReminderSelection = (requestId) => {
    setSelectedReminders(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const selectAllReminders = () => {
    if (selectedReminders.length === pendingReminders.length) {
      setSelectedReminders([]);
    } else {
      setSelectedReminders(pendingReminders.map(r => r.id));
    }
  };

  const handleUpdateFeedbackAction = async (id, action) => {
    try {
      await api.put(`/feedback/${id}/action`, null, {
        params: { action_taken: action, status: 'afgehandeld' }
      });
      toast.success('Actie opgeslagen');
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleCreateTaskFromFeedback = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/feedback/${selectedFeedback.id}/create-task`, null, {
        params: {
          assigned_to: taskForm.assigned_to || null,
          due_date: taskForm.due_date || null,
          priority: taskForm.priority
        }
      });
      toast.success('Taak aangemaakt en toegewezen');
      setTaskDialog(false);
      setSelectedFeedback(null);
      setTaskForm({ assigned_to: '', due_date: '', priority: 'hoog' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij aanmaken taak');
    }
  };

  const openTaskDialog = (fb) => {
    setSelectedFeedback(fb);
    setTaskForm({ assigned_to: '', due_date: '', priority: 'hoog' });
    setTaskDialog(true);
  };

  const handleAddReviewRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post('/review-requests', null, { 
        params: {
          ...requestForm,
          channel: requestForm.channel || 'email',
          sent_by: requestForm.sent_by || null
        }
      });
      toast.success('Review verzoek toegevoegd');
      setRequestDialog(false);
      setRequestForm({ guest_name: '', guest_email: '', guest_phone: '', visit_date: '', notes: '', channel: 'email', sent_by: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleUpdateRequestStatus = async (id, status, received = false, channel = null, sentBy = null) => {
    try {
      await api.put(`/review-requests/${id}/status`, null, {
        params: { 
          status, 
          review_received: received,
          channel: channel,
          sent_by: sentBy
        }
      });
      toast.success('Status bijgewerkt');
      setSendDialog(false);
      setRequestDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const openRequestDetail = (request) => {
    setSelectedRequest(request);
    setRequestDetailDialog(true);
  };

  const openSendDialog = (request) => {
    setSelectedRequest(request);
    setSendForm({ channel: 'email', sent_by: '' });
    setSendDialog(true);
  };

  const handleSendRequest = async () => {
    if (!selectedRequest) return;
    await handleUpdateRequestStatus(
      selectedRequest.id, 
      'verzonden', 
      false, 
      sendForm.channel,
      sendForm.sent_by || null
    );
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Verzoek verwijderen?')) return;
    try {
      await api.delete(`/review-requests/${id}`);
      toast.success('Verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const openEditRequest = (request) => {
    setEditRequestForm({
      id: request.id,
      guest_name: request.guest_name || '',
      guest_email: request.guest_email || '',
      guest_phone: request.guest_phone || '',
      visit_date: request.visit_date || '',
      notes: request.notes || '',
      channel: request.channel || 'email',
      sent_by: request.sent_by || ''
    });
    setEditRequestDialog(true);
  };

  const handleEditRequest = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/review-requests/${editRequestForm.id}`, null, {
        params: {
          guest_name: editRequestForm.guest_name,
          guest_email: editRequestForm.guest_email,
          guest_phone: editRequestForm.guest_phone,
          visit_date: editRequestForm.visit_date,
          notes: editRequestForm.notes,
          channel: editRequestForm.channel,
          sent_by: editRequestForm.sent_by || null
        }
      });
      toast.success('Review verzoek bijgewerkt');
      setEditRequestDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const copyReviewLink = () => {
    if (businessSettings?.google_business_url) {
      navigator.clipboard.writeText(businessSettings.google_business_url);
      toast.success('Review link gekopieerd!');
    }
  };

  // Calculate metrics
  const totalGuests = guests.reduce((sum, g) => sum + (g.guest_count || 0), 0);
  const googleRating = businessSettings?.google_rating;
  const avgRating = googleRating || (reviews.length > 0 
    ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length 
    : 0);
  const klachten = feedback.filter(f => f.type === 'klacht').length;
  const openKlachten = feedback.filter(f => f.type === 'klacht' && f.status !== 'afgehandeld').length;
  const complimenten = feedback.filter(f => f.type === 'compliment').length;
  const returningCount = returningGuests.length;
  const multiVisitGuests = returningGuests.filter(g => g.visits && g.visits.length > 1).length;
  const returningPercentage = returningCount > 0 ? (multiVisitGuests / returningCount) * 100 : 0;

  // Guest data by date
  const guestChartData = guests
    .reduce((acc, g) => {
      const existing = acc.find(a => a.date === g.date);
      if (existing) {
        existing.gasten += g.guest_count || 0;
      } else {
        acc.push({ date: g.date, gasten: g.guest_count || 0 });
      }
      return acc;
    }, [])
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-14)
    .map(d => ({ ...d, label: new Date(d.date).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' }) }));

  // Feedback type data
  const feedbackData = [
    { name: 'Klachten', value: klachten, color: '#EF4444' },
    { name: 'Complimenten', value: complimenten, color: '#22C55E' }
  ];

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="gast-beleving-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Gast & Beleving
          </h1>
          <p className="text-[#57534E] mt-1">
            Beheer gastgegevens, reviews en feedback
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setSettingsDialog(true)}
            className="border-stone-300"
          >
            <Settings className="w-4 h-4 mr-2" />
            Google Koppelen
          </Button>
          <Button 
            onClick={handleGenerateDemoReviews}
            disabled={importing || !businessSettings?.google_business_url}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="generate-demo-reviews-btn"
            title="Genereert AI-gegenereerde demo reviews voor test doeleinden"
          >
            {importing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Genereren...</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-2" />Demo Reviews Genereren</>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportToPDF('Gasten',
            [{ header: 'Datum', key: 'date' }, { header: 'Aantal', key: 'guest_count' }],
            guests.map(g => ({ ...g, date: formatDate(g.date) })), 'gasten'
          )}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Google Business Card */}
      {businessSettings?.google_business_url && (
        <Card className="card-base bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{businessSettings.business_name || 'Uw Bedrijf'}</p>
                  <div className="flex items-center gap-4 mt-1">
                    {businessSettings.google_rating && (
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                        <span className="font-bold text-lg">{businessSettings.google_rating.toFixed(1)}</span>
                        <span className="text-[#57534E]">({businessSettings.total_reviews} reviews)</span>
                      </div>
                    )}
                    {businessSettings.last_import && (
                      <span className="text-sm text-[#57534E]">
                        Laatst geïmporteerd: {formatDate(businessSettings.last_import)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={copyReviewLink} title="Kopieer review link">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Link kopiëren
                </Button>
                <a 
                  href={businessSettings.google_business_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 p-2"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review Calculator Card */}
      {reviewCalculator && businessSettings?.google_rating && (
        <Card className="card-base bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-600" />
                  Reviews Nodig Calculator
                </h3>
                <p className="text-sm text-[#57534E] mt-1">
                  Hoeveel 5-sterren reviews nodig om je rating te verhogen?
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {reviewCalculator.targets?.map((t, i) => (
                <div key={i} className="bg-white rounded-lg p-3 text-center shadow-sm">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="font-bold text-lg">{t.target}</span>
                  </div>
                  <p className="text-2xl font-bold text-[#C2410C]">{t.reviews_needed}</p>
                  <p className="text-xs text-[#57534E]">reviews nodig</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#57534E] mt-3 text-center">
              💡 Tip: Stuur review verzoeken naar tevreden gasten om sneller je doelrating te bereiken!
            </p>
          </CardContent>
        </Card>
      )}

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="kpi-tile">
          <p className="kpi-label">Totaal Gasten</p>
          <p className="kpi-value">{formatNumber(totalGuests)}</p>
          <p className="text-sm text-[#57534E]">deze periode</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Terugkerende Gasten</p>
          <p className="kpi-value">{formatPercentage(returningPercentage)}</p>
          <p className="text-sm text-[#57534E]">{multiVisitGuests} van {returningCount}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Google Rating</p>
          <div className="flex items-center gap-2">
            <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
            <p className="kpi-value text-amber-600">{avgRating.toFixed(1)}</p>
          </div>
          <p className="text-sm text-[#57534E]">{businessSettings?.total_reviews || reviews.length} reviews</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Open Klachten</p>
          <p className={`kpi-value ${openKlachten > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{openKlachten}</p>
          <p className="text-sm text-[#57534E]">{klachten} totaal</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Complimenten</p>
          <p className="kpi-value text-emerald-600">{complimenten}</p>
          <p className="text-sm text-[#57534E]">ontvangen</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="returning">Terugkerende Gasten</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="requests" className="flex items-center gap-1">
            <Send className="w-3 h-3" />
            Review Verzoeken
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Manrope' }}>Gasten per Dag</CardTitle>
                <Dialog open={guestDialog} onOpenChange={setGuestDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" size="sm" data-testid="add-guest-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Toevoegen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Gastenaantal invoeren</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddGuest} className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Datum</Label>
                        <Input
                          type="date"
                          value={guestForm.date}
                          onChange={(e) => setGuestForm({ ...guestForm, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Aantal gasten</Label>
                        <Input
                          type="number"
                          value={guestForm.guest_count}
                          onChange={(e) => setGuestForm({ ...guestForm, guest_count: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Locatie (optioneel)</Label>
                        <Select
                          value={guestForm.location_id || "none"}
                          onValueChange={(value) => setGuestForm({ ...guestForm, location_id: value === "none" ? "" : value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer locatie" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Geen</SelectItem>
                            {locations.map(loc => (
                              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="w-full btn-primary">
                        Opslaan
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={guestChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#57534E' }} />
                      <Tooltip />
                      <Bar dataKey="gasten" fill="#C2410C" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Feedback Verdeling</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={feedbackData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {feedbackData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Returning Guests Tab */}
        <TabsContent value="returning" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Terugkerende Gasten</CardTitle>
              <Dialog open={returningDialog} onOpenChange={setReturningDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-returning-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Gast toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Terugkerende gast toevoegen</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddReturning} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Naam</Label>
                      <Input
                        value={returningForm.name}
                        onChange={(e) => setReturningForm({ ...returningForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={returningForm.email}
                          onChange={(e) => setReturningForm({ ...returningForm, email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefoon</Label>
                        <Input
                          value={returningForm.phone}
                          onChange={(e) => setReturningForm({ ...returningForm, phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Voorkeuren</Label>
                      <Input
                        value={returningForm.preferences}
                        onChange={(e) => setReturningForm({ ...returningForm, preferences: e.target.value })}
                        placeholder="bijv. tafelplek, dieetwensen"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notities</Label>
                      <Textarea
                        value={returningForm.notes}
                        onChange={(e) => setReturningForm({ ...returningForm, notes: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {returningGuests.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-[#57534E]">
                    Nog geen terugkerende gasten geregistreerd
                  </div>
                ) : (
                  returningGuests.map(guest => (
                    <Card key={guest.id} className="card-base">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#C2410C]/10 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-[#C2410C]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold">{guest.name}</h3>
                            {guest.email && <p className="text-sm text-[#57534E]">{guest.email}</p>}
                            <p className="text-sm text-[#C2410C] mt-1">
                              {guest.visits?.length || 0} bezoeken
                            </p>
                            {guest.preferences && (
                              <p className="text-xs text-[#57534E] mt-2 italic">
                                {guest.preferences}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Reviews & Ratings</CardTitle>
              <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-review-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Review toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Review toevoegen</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddReview} className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Datum</Label>
                        <Input
                          type="date"
                          value={reviewForm.date}
                          onChange={(e) => setReviewForm({ ...reviewForm, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Platform</Label>
                        <Select
                          value={reviewForm.platform}
                          onValueChange={(value) => setReviewForm({ ...reviewForm, platform: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Google">Google</SelectItem>
                            <SelectItem value="TripAdvisor">TripAdvisor</SelectItem>
                            <SelectItem value="Iens">Iens</SelectItem>
                            <SelectItem value="Facebook">Facebook</SelectItem>
                            <SelectItem value="Overig">Overig</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Rating (1-5)</Label>
                        <Input
                          type="number"
                          min="1"
                          max="5"
                          step="0.1"
                          value={reviewForm.rating}
                          onChange={(e) => setReviewForm({ ...reviewForm, rating: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Naam gast (optioneel)</Label>
                        <Input
                          value={reviewForm.guest_name}
                          onChange={(e) => setReviewForm({ ...reviewForm, guest_name: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Review tekst</Label>
                      <Textarea
                        value={reviewForm.content}
                        onChange={(e) => setReviewForm({ ...reviewForm, content: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Gast</TableHead>
                    <TableHead>Review</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#57534E]">
                        Nog geen reviews
                      </TableCell>
                    </TableRow>
                  ) : (
                    reviews.sort((a, b) => new Date(b.date) - new Date(a.date)).map(review => (
                      <TableRow key={review.id} className="table-row">
                        <TableCell>{formatDate(review.date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{review.platform}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            <span className="font-semibold">{review.rating}</span>
                          </div>
                        </TableCell>
                        <TableCell>{review.guest_name || '-'}</TableCell>
                        <TableCell className="max-w-xs truncate">{review.content || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Klachten & Complimenten</CardTitle>
              <Dialog open={feedbackDialog} onOpenChange={setFeedbackDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-feedback-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Feedback toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Feedback toevoegen</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddFeedback} className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Datum</Label>
                        <Input
                          type="date"
                          value={feedbackForm.date}
                          onChange={(e) => setFeedbackForm({ ...feedbackForm, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={feedbackForm.type}
                          onValueChange={(value) => setFeedbackForm({ ...feedbackForm, type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="klacht">Klacht</SelectItem>
                            <SelectItem value="compliment">Compliment</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Naam gast (optioneel)</Label>
                      <Input
                        value={feedbackForm.guest_name}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, guest_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Inhoud</Label>
                      <Textarea
                        value={feedbackForm.content}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, content: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Gast</TableHead>
                    <TableHead>Inhoud</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actie</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedback.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-[#57534E]">
                        Nog geen feedback
                      </TableCell>
                    </TableRow>
                  ) : (
                    feedback.sort((a, b) => new Date(b.date) - new Date(a.date)).map(fb => (
                      <TableRow key={fb.id} className="table-row">
                        <TableCell>{formatDate(fb.date)}</TableCell>
                        <TableCell>
                          {fb.type === 'klacht' ? (
                            <Badge className="badge-error"><ThumbsDown className="w-3 h-3 mr-1" />Klacht</Badge>
                          ) : (
                            <Badge className="badge-success"><ThumbsUp className="w-3 h-3 mr-1" />Compliment</Badge>
                          )}
                        </TableCell>
                        <TableCell>{fb.guest_name || '-'}</TableCell>
                        <TableCell className="max-w-xs truncate">{fb.content}</TableCell>
                        <TableCell>
                          <Badge className={fb.status === 'afgehandeld' ? 'badge-success' : fb.status === 'in_behandeling' ? 'bg-blue-100 text-blue-700' : 'badge-warning'}>
                            {fb.status === 'in_behandeling' ? 'In behandeling' : fb.status || 'nieuw'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {fb.status !== 'afgehandeld' && fb.type === 'klacht' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openTaskDialog(fb)}
                                  title="Taak aanmaken"
                                >
                                  <ArrowRight className="w-3 h-3 mr-1" />
                                  Taak
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-emerald-600"
                                  onClick={() => {
                                    const action = prompt('Welke actie is ondernomen?');
                                    if (action) handleUpdateFeedbackAction(fb.id, action);
                                  }}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Afhandelen
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Review Requests Tab */}
        <TabsContent value="requests" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Review Verzoeken</CardTitle>
              <Dialog open={requestDialog} onOpenChange={setRequestDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-request-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Verzoek toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Review verzoek toevoegen</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddReviewRequest} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Naam gast</Label>
                      <Input
                        value={requestForm.guest_name}
                        onChange={(e) => setRequestForm({ ...requestForm, guest_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={requestForm.guest_email}
                          onChange={(e) => setRequestForm({ ...requestForm, guest_email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefoon</Label>
                        <Input
                          value={requestForm.guest_phone}
                          onChange={(e) => setRequestForm({ ...requestForm, guest_phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Bezoekdatum</Label>
                      <Input
                        type="date"
                        value={requestForm.visit_date}
                        onChange={(e) => setRequestForm({ ...requestForm, visit_date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Kanaal</Label>
                        <Select value={requestForm.channel} onValueChange={(value) => setRequestForm({ ...requestForm, channel: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer kanaal" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="whatsapp">WhatsApp</SelectItem>
                            <SelectItem value="persoonlijk">Persoonlijk</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Verzonden door</Label>
                        <Select value={requestForm.sent_by} onValueChange={(value) => setRequestForm({ ...requestForm, sent_by: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer medewerker" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notities</Label>
                      <Textarea
                        value={requestForm.notes}
                        onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                        placeholder="Bijzonderheden van het bezoek..."
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Verzoek toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {/* Stats Cards */}
              {reviewRequestStats && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  <div className="kpi-tile">
                    <p className="kpi-label">Totaal Verzoeken</p>
                    <p className="kpi-value">{reviewRequestStats.total_requests}</p>
                  </div>
                  <div className="kpi-tile">
                    <p className="kpi-label">Verzonden</p>
                    <p className="kpi-value text-blue-600">{reviewRequestStats.sent}</p>
                  </div>
                  <div className="kpi-tile">
                    <p className="kpi-label">Reviews Ontvangen</p>
                    <p className="kpi-value text-emerald-600">{reviewRequestStats.received}</p>
                  </div>
                  <div className="kpi-tile">
                    <p className="kpi-label">Conversie Rate</p>
                    <p className="kpi-value text-[#C2410C]">{reviewRequestStats.conversion_rate}%</p>
                  </div>
                  <div className="kpi-tile border-amber-200 bg-amber-50">
                    <p className="kpi-label text-amber-700">Wachtend op Herinnering</p>
                    <p className="kpi-value text-amber-600">{reviewRequestStats.pending_reminders || 0}</p>
                  </div>
                </div>
              )}

              {/* Pending Reminders Alert */}
              {pendingReminders.length > 0 && (
                <Card className="mb-6 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-amber-800">
                        <Bell className="w-5 h-5" />
                        Automatische Herinneringen
                        <Badge className="bg-amber-200 text-amber-800">{pendingReminders.length}</Badge>
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReminderDialog(true)}
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Instellingen
                        </Button>
                        {selectedReminders.length > 0 && (
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={() => setBulkReminderDialog(true)}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Verzend {selectedReminders.length} herinneringen
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-amber-700 mb-4">
                      Deze gasten hebben {reminderSettings.reminder_days}+ dagen geleden een review verzoek ontvangen maar nog geen review achtergelaten.
                    </p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={selectedReminders.length === pendingReminders.length && pendingReminders.length > 0}
                          onChange={selectAllReminders}
                          className="w-4 h-4 rounded border-amber-300"
                        />
                        <span className="text-sm font-medium text-amber-800">Selecteer alles</span>
                      </div>
                      
                      {pendingReminders.map(request => (
                        <div 
                          key={request.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            selectedReminders.includes(request.id) 
                              ? 'bg-amber-100 border-amber-400' 
                              : 'bg-white border-amber-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedReminders.includes(request.id)}
                              onChange={() => toggleReminderSelection(request.id)}
                              className="w-4 h-4 rounded border-amber-300"
                            />
                            <div>
                              <p className="font-medium">{request.guest_name}</p>
                              <p className="text-sm text-[#57534E]">
                                {request.guest_email || request.guest_phone}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm font-medium text-amber-700">
                                {request.days_since_sent} dagen geleden
                              </p>
                              <p className="text-xs text-[#57534E]">
                                Herinneringen: {request.reminder_count || 0}/{reminderSettings.max_reminders}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendReminder(request.id)}
                              className="text-amber-600"
                            >
                              <Bell className="w-3 h-3 mr-1" />
                              Herinner
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Gast</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Bezoekdatum</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Kanaal</TableHead>
                    <TableHead>Verzonden door</TableHead>
                    <TableHead>Verzonden op</TableHead>
                    <TableHead>Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviewRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-[#57534E]">
                        Nog geen review verzoeken
                      </TableCell>
                    </TableRow>
                  ) : (
                    reviewRequests.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(request => (
                      <TableRow 
                        key={request.id} 
                        className="table-row cursor-pointer hover:bg-stone-50 transition-colors"
                        onClick={() => openEditRequest(request)}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">{request.guest_name}</p>
                            {request.notes && (
                              <p className="text-sm text-[#57534E] truncate max-w-xs">{request.notes}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {request.guest_email && (
                              <p className="flex items-center gap-1 text-blue-600">
                                <Mail className="w-3 h-3" />
                                {request.guest_email}
                              </p>
                            )}
                            {request.guest_phone && (
                              <p className="flex items-center gap-1 text-[#57534E]">
                                <Phone className="w-3 h-3" />
                                {request.guest_phone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(request.visit_date)}</TableCell>
                        <TableCell>
                          <Badge className={
                            request.status === 'verzonden' ? 'bg-blue-100 text-blue-700' :
                            request.status === 'herinnering' ? 'bg-amber-100 text-amber-700' :
                            request.status === 'review_ontvangen' || request.review_received ? 'badge-success' :
                            'bg-stone-100 text-stone-600'
                          }>
                            {request.status === 'verzonden' ? 'Verzonden' :
                             request.status === 'herinnering' ? 'Herinnering' :
                             request.status === 'review_ontvangen' || request.review_received ? 'Review ontvangen' :
                             'Concept'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {request.channel ? (
                            <Badge variant="outline" className="capitalize">
                              {request.channel === 'email' && <Mail className="w-3 h-3 mr-1" />}
                              {request.channel === 'sms' && <MessageCircle className="w-3 h-3 mr-1" />}
                              {request.channel === 'whatsapp' && <MessageCircle className="w-3 h-3 mr-1" />}
                              {request.channel === 'persoonlijk' && <User className="w-3 h-3 mr-1" />}
                              {request.channel}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {request.sent_by_name || request.sent_by ? (
                            <span className="text-sm">{request.sent_by_name || 'Medewerker'}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {request.sent_at ? formatDate(request.sent_at) : '-'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditRequest(request)}
                              title="Bewerken"
                              className="text-[#C2410C]"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRequestDetail(request)}
                              title="Details bekijken"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            {request.status === 'concept' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openSendDialog(request)}
                                className="text-blue-600"
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Verzenden
                              </Button>
                            )}
                            {request.status === 'verzonden' && !request.review_received && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateRequestStatus(request.id, 'herinnering')}
                                  className="text-amber-600"
                                  title="Markeer als herinnering verzonden"
                                >
                                  <Clock className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateRequestStatus(request.id, 'review_ontvangen', true)}
                                  className="text-emerald-600"
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Ontvangen
                                </Button>
                              </>
                            )}
                            {request.status === 'herinnering' && !request.review_received && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateRequestStatus(request.id, 'review_ontvangen', true)}
                                className="text-emerald-600"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Ontvangen
                              </Button>
                            )}
                            {businessSettings?.google_business_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={copyReviewLink}
                                title="Kopieer review link"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteRequest(request.id)}
                              className="text-rose-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Review Request Dialog */}
      <Dialog open={editRequestDialog} onOpenChange={setEditRequestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#C2410C]" />
              Review Verzoek Bewerken
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditRequest} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Gastnaam *</Label>
              <Input
                value={editRequestForm.guest_name}
                onChange={(e) => setEditRequestForm({ ...editRequestForm, guest_name: e.target.value })}
                placeholder="Naam van de gast"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editRequestForm.guest_email}
                  onChange={(e) => setEditRequestForm({ ...editRequestForm, guest_email: e.target.value })}
                  placeholder="email@voorbeeld.nl"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefoon</Label>
                <Input
                  value={editRequestForm.guest_phone}
                  onChange={(e) => setEditRequestForm({ ...editRequestForm, guest_phone: e.target.value })}
                  placeholder="+31 6..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bezoekdatum</Label>
              <Input
                type="date"
                value={editRequestForm.visit_date}
                onChange={(e) => setEditRequestForm({ ...editRequestForm, visit_date: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kanaal</Label>
                <Select value={editRequestForm.channel} onValueChange={(value) => setEditRequestForm({ ...editRequestForm, channel: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer kanaal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="persoonlijk">Persoonlijk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Verzonden door</Label>
                <Select value={editRequestForm.sent_by} onValueChange={(value) => setEditRequestForm({ ...editRequestForm, sent_by: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer medewerker" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={editRequestForm.notes}
                onChange={(e) => setEditRequestForm({ ...editRequestForm, notes: e.target.value })}
                placeholder="Bijzonderheden van het bezoek..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditRequestDialog(false)}>
                Annuleren
              </Button>
              <Button type="submit" className="btn-primary">
                Opslaan
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Google Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Google Business Koppelen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-[#57534E]">
              Koppel uw Google Business pagina om demo reviews te genereren met AI. Deze reviews zijn voorbeelden voor test doeleinden.
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Let op:</strong> Dit genereert AI-gegenereerde demo reviews, geen echte Google reviews. Voor echte integratie is de Google My Business API vereist.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Google Business URL</Label>
              <Input
                value={googleUrl}
                onChange={(e) => setGoogleUrl(e.target.value)}
                placeholder="https://www.google.com/maps/place/..."
              />
              <p className="text-xs text-[#57534E]">
                Tip: Zoek uw bedrijf op Google Maps en kopieer de URL uit de adresbalk.
              </p>
            </div>
            {businessSettings?.last_import && (
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  Laatst gegenereerd: {formatDate(businessSettings.last_import)}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <DialogClose asChild>
                <Button variant="outline">Annuleren</Button>
              </DialogClose>
              <Button onClick={handleSaveGoogleUrl} className="btn-primary">
                Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Creation Dialog */}
      <Dialog open={taskDialog} onOpenChange={(open) => { setTaskDialog(open); if (!open) setSelectedFeedback(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Taak aanmaken van klacht
            </DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <form onSubmit={handleCreateTaskFromFeedback} className="space-y-4 pt-4">
              <div className="p-3 bg-rose-50 rounded-lg">
                <p className="font-medium text-sm text-rose-800">Klacht van: {selectedFeedback.guest_name || 'Anoniem'}</p>
                <p className="text-sm text-rose-700 mt-1 line-clamp-3">{selectedFeedback.content}</p>
                {selectedFeedback.source && (
                  <Badge variant="outline" className="mt-2 text-xs">{selectedFeedback.source}</Badge>
                )}
              </div>

              <div className="space-y-2">
                <Label>Toewijzen aan medewerker</Label>
                <Select value={taskForm.assigned_to} onValueChange={(v) => setTaskForm({ ...taskForm, assigned_to: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecteer medewerker..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niet toegewezen</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prioriteit</Label>
                  <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laag">Laag</SelectItem>
                      <SelectItem value="normaal">Normaal</SelectItem>
                      <SelectItem value="hoog">Hoog</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-sm text-[#57534E]">
                De taak wordt aangemaakt in <strong>Doelen & Structuur</strong>.
              </p>

              <div className="flex justify-end gap-2 pt-4">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Annuleren</Button>
                </DialogClose>
                <Button type="submit" className="btn-primary">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Taak aanmaken
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={requestDetailDialog} onOpenChange={setRequestDetailDialog}>
        <DialogContent className="max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle>Review Verzoek Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 pt-4 overflow-hidden">
              {/* Guest Info Header */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C2410C] to-[#D97706] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {selectedRequest.guest_name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-[#1C1917] truncate">{selectedRequest.guest_name}</h3>
                  <Badge className={
                    selectedRequest.status === 'verzonden' ? 'bg-blue-100 text-blue-700' :
                    selectedRequest.status === 'herinnering' ? 'bg-amber-100 text-amber-700' :
                    selectedRequest.status === 'review_ontvangen' || selectedRequest.review_received ? 'bg-emerald-100 text-emerald-700' :
                    'bg-stone-100 text-stone-600'
                  }>
                    {selectedRequest.status === 'verzonden' ? 'Verzonden' :
                     selectedRequest.status === 'herinnering' ? 'Herinnering verzonden' :
                     selectedRequest.status === 'review_ontvangen' || selectedRequest.review_received ? 'Review ontvangen' :
                     'Concept'}
                  </Badge>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 overflow-hidden">
                <h4 className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">Contactgegevens</h4>
                <div className="bg-stone-50 rounded-lg p-3 overflow-hidden">
                  {selectedRequest.guest_email && (
                    <div className="flex items-center py-1">
                      <div className="flex items-center gap-2 w-24 flex-shrink-0">
                        <Mail className="w-4 h-4 text-[#C2410C]" />
                        <span className="text-xs text-[#78716C]">Email</span>
                      </div>
                      <span className="font-medium text-sm text-[#1C1917] truncate">{selectedRequest.guest_email}</span>
                    </div>
                  )}
                  {selectedRequest.guest_phone && (
                    <div className="flex items-center py-1">
                      <div className="flex items-center gap-2 w-24 flex-shrink-0">
                        <Phone className="w-4 h-4 text-[#C2410C]" />
                        <span className="text-xs text-[#78716C]">Telefoon</span>
                      </div>
                      <span className="font-medium text-sm text-[#1C1917]">{selectedRequest.guest_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Request Info */}
              <div className="space-y-2 overflow-hidden">
                <h4 className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">Verzoek Details</h4>
                <div className="bg-stone-50 rounded-lg p-3 overflow-hidden">
                  <div className="flex items-center py-1">
                    <div className="flex items-center gap-2 w-24 flex-shrink-0">
                      <Calendar className="w-4 h-4 text-[#C2410C]" />
                      <span className="text-xs text-[#78716C]">Datum</span>
                    </div>
                    <span className="font-medium text-sm text-[#1C1917]">{formatDate(selectedRequest.visit_date) || '-'}</span>
                  </div>
                  <div className="flex items-center py-1">
                    <div className="flex items-center gap-2 w-24 flex-shrink-0">
                      <MessageCircle className="w-4 h-4 text-[#C2410C]" />
                      <span className="text-xs text-[#78716C]">Kanaal</span>
                    </div>
                    <span className="font-medium text-sm text-[#1C1917] capitalize">{selectedRequest.channel || '-'}</span>
                  </div>
                  {(selectedRequest.status !== 'concept') && (
                    <>
                      <div className="flex items-center py-1">
                        <div className="flex items-center gap-2 w-24 flex-shrink-0">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span className="text-xs text-blue-600">Verzonden</span>
                        </div>
                        <span className="font-medium text-sm text-blue-800">{selectedRequest.sent_at ? formatDate(selectedRequest.sent_at) : '-'}</span>
                      </div>
                      <div className="flex items-center py-1">
                        <div className="flex items-center gap-2 w-24 flex-shrink-0">
                          <User className="w-4 h-4 text-purple-600" />
                          <span className="text-xs text-purple-600">Door</span>
                        </div>
                        <span className="font-medium text-sm text-purple-800">{selectedRequest.sent_by_name || '-'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedRequest.notes && (
                <div className="space-y-2 overflow-hidden">
                  <h4 className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">Notities</h4>
                  <p className="p-3 bg-stone-50 rounded-lg text-sm text-[#1C1917] break-words">{selectedRequest.notes}</p>
                </div>
              )}

              {/* Review Link */}
              {selectedRequest.review_link && (
                <div className="space-y-2 overflow-hidden">
                  <h4 className="text-xs font-semibold text-[#78716C] uppercase tracking-wide">Review Link</h4>
                  <a 
                    href={selectedRequest.review_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 bg-blue-50 rounded-lg text-blue-600 hover:bg-blue-100 transition-colors overflow-hidden"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm truncate block">{selectedRequest.review_link}</span>
                    </div>
                  </a>
                </div>
              )}

              {/* Status Update Actions */}
              <div className="space-y-3 pt-4 border-t">
                <h4 className="text-sm font-semibold text-[#78716C] uppercase">Status Wijzigen</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.status === 'concept' && (
                    <Button
                      variant="outline"
                      className="text-blue-600"
                      onClick={() => {
                        setRequestDetailDialog(false);
                        openSendDialog(selectedRequest);
                      }}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Markeer als verzonden
                    </Button>
                  )}
                  {selectedRequest.status === 'verzonden' && (
                    <>
                      <Button
                        variant="outline"
                        className="text-amber-600"
                        onClick={() => handleUpdateRequestStatus(selectedRequest.id, 'herinnering')}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        Herinnering verzonden
                      </Button>
                      <Button
                        variant="outline"
                        className="text-emerald-600"
                        onClick={() => handleUpdateRequestStatus(selectedRequest.id, 'review_ontvangen', true)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Review ontvangen
                      </Button>
                    </>
                  )}
                  {selectedRequest.status === 'herinnering' && !selectedRequest.review_received && (
                    <Button
                      variant="outline"
                      className="text-emerald-600"
                      onClick={() => handleUpdateRequestStatus(selectedRequest.id, 'review_ontvangen', true)}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Review ontvangen
                    </Button>
                  )}
                </div>
              </div>

              <Button variant="outline" className="w-full" onClick={() => setRequestDetailDialog(false)}>
                Sluiten
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Request Dialog */}
      <Dialog open={sendDialog} onOpenChange={setSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Verzoek Verzenden
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 pt-4">
              <p className="text-sm text-[#57534E]">
                Markeer het review verzoek voor <strong>{selectedRequest.guest_name}</strong> als verzonden.
              </p>
              
              <div className="space-y-2">
                <Label>Via welk kanaal is het verzoek verzonden?</Label>
                <Select 
                  value={sendForm.channel} 
                  onValueChange={(v) => setSendForm({ ...sendForm, channel: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer kanaal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </div>
                    </SelectItem>
                    <SelectItem value="sms">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        SMS
                      </div>
                    </SelectItem>
                    <SelectItem value="whatsapp">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                      </div>
                    </SelectItem>
                    <SelectItem value="persoonlijk">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Persoonlijk
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Door welke medewerker?</Label>
                <Select 
                  value={sendForm.sent_by || "none"} 
                  onValueChange={(v) => setSendForm({ ...sendForm, sent_by: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer medewerker" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niet opgegeven</SelectItem>
                    {employees.filter(e => e.is_active).map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setSendDialog(false)}>
                  Annuleren
                </Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSendRequest}>
                  <Send className="w-4 h-4 mr-2" />
                  Verzonden markeren
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reminder Settings Dialog */}
      <Dialog open={reminderDialog} onOpenChange={setReminderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-600" />
              Herinnering Instellingen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <p className="text-sm text-[#57534E]">
              Configureer wanneer gasten een herinnering moeten krijgen om een review achter te laten.
            </p>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Dagen na verzenden verzoek</Label>
                <p className="text-xs text-[#78716C]">
                  Na hoeveel dagen zonder review verschijnt een gast in de herinneringen lijst?
                </p>
                <Select 
                  value={String(reminderSettings.reminder_days)}
                  onValueChange={(v) => setReminderSettings(prev => ({ ...prev, reminder_days: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 dag</SelectItem>
                    <SelectItem value="2">2 dagen</SelectItem>
                    <SelectItem value="3">3 dagen</SelectItem>
                    <SelectItem value="5">5 dagen</SelectItem>
                    <SelectItem value="7">7 dagen (1 week)</SelectItem>
                    <SelectItem value="14">14 dagen (2 weken)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Maximum aantal herinneringen</Label>
                <p className="text-xs text-[#78716C]">
                  Hoeveel herinneringen mag een gast maximaal ontvangen?
                </p>
                <Select 
                  value={String(reminderSettings.max_reminders)}
                  onValueChange={(v) => setReminderSettings(prev => ({ ...prev, max_reminders: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 herinnering</SelectItem>
                    <SelectItem value="2">2 herinneringen</SelectItem>
                    <SelectItem value="3">3 herinneringen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setReminderDialog(false)}>
                Annuleren
              </Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={handleUpdateReminderSettings}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Reminder Dialog */}
      <Dialog open={bulkReminderDialog} onOpenChange={setBulkReminderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Herinneringen Verzenden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-[#57534E]">
              Je staat op het punt om <strong>{selectedReminders.length}</strong> herinneringen te verzenden.
            </p>

            <div className="space-y-2">
              <Label>Via welk kanaal?</Label>
              <Select 
                value={bulkReminderForm.channel}
                onValueChange={(v) => setBulkReminderForm(prev => ({ ...prev, channel: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </div>
                  </SelectItem>
                  <SelectItem value="sms">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      SMS
                    </div>
                  </SelectItem>
                  <SelectItem value="whatsapp">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Door welke medewerker?</Label>
              <Select 
                value={bulkReminderForm.sent_by || "none"}
                onValueChange={(v) => setBulkReminderForm(prev => ({ ...prev, sent_by: v === "none" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer medewerker" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Niet opgegeven</SelectItem>
                  {employees.filter(e => e.is_active).map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Let op:</strong> Dit markeert de herinneringen als verzonden. 
                Zorg ervoor dat je de herinneringen daadwerkelijk verstuurt via het gekozen kanaal.
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setBulkReminderDialog(false)}>
                Annuleren
              </Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={handleSendBulkReminders}>
                <Send className="w-4 h-4 mr-2" />
                Verzend {selectedReminders.length} herinneringen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* AI Key Required Dialog */}
      <AIKeyRequiredDialog 
        open={showAIKeyDialog} 
        onOpenChange={setShowAIKeyDialog}
        feature="Demo Reviews genereren"
      />
    </div>
  );
};

export default GastBelevingPage;
